import React, { useState, useEffect, useRef } from "react";
import MiniDrawer from "../../../components/MiniDrawer/MiniDrawer";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { DateRangePicker } from "react-date-range";
import * as XLSX from "xlsx";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const baseAPI = process.env.REACT_APP_API_URL;
const baseImageURL = process.env.REACT_APP_IMAGE_URL;
const storedToken = JSON.parse(localStorage.getItem("token"));

const EXPENSE_MODES = ["All", "Bank Transfer", "Online Payment", "Cash", "Cheque", "UPI"];

const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toISOString().split("T")[0];
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "2-digit" });
};

const formatCurrency = (amount) => {
  if (amount == null) return "-";
  return `₹ ${Number(amount).toLocaleString("en-IN")}`;
};

export default function IndirectExpense() {
  const navigate = useNavigate();
  const location = useLocation();

  // List state
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [expenseMode, setExpenseMode] = useState("All");
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    { startDate: null, endDate: null, key: "selection" },
  ]);
  const datePickerRef = useRef(null);
  const modeDropdownRef = useRef(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [form, setForm] = useState({
    description: "",
    amount: "",
    expense_mode: "Bank Transfer",
    expense_date: "",
    remark: "",
    attachment: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [attachmentFile, setAttachmentFile] = useState(null);

  // Edit state — null means "Add" mode, otherwise holds the id being edited
  const [editingId, setEditingId] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null); // the expense object pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(e.target)) {
        setShowModeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchExpenses = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      // Only append filters if they have values — never send empty strings
      params.append("indirect_expense_id", 0);
      if (dateRange[0].startDate) params.append("from_date", formatDate(dateRange[0].startDate));
      if (dateRange[0].endDate) params.append("to_date", formatDate(dateRange[0].endDate));
      if (expenseMode !== "All") params.append("expense_mode", expenseMode);

      // Strip any trailing slash from baseAPI to avoid double-slash
      const cleanBase = (baseAPI || "").replace(/\/+$/, "");
      const queryString = params.toString();
      const url = `${cleanBase}/userget/GetIndirectExpense${queryString ? `?${queryString}` : ""}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${storedToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch expenses");
      const data = await res.json();
      setExpenses(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError("Failed to load expenses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line
  }, [expenseMode, dateRange]);

  const handleDateRangeChange = (item) => {
    setDateRange([item.selection]);
  };

  const clearDateRange = () => {
    setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
    setShowDatePicker(false);
  };

  const getDateRangeLabel = () => {
    const { startDate, endDate } = dateRange[0];
    if (!startDate && !endDate) return "Select Date Range";
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
    }
    return formatDisplayDate(startDate);
  };

  // Download Excel
  const handleDownloadExcel = () => {
    const rows = expenses.map((e, i) => ({
      "S.No": i + 1,
      "Date (Expense Added)": formatDisplayDate(e.created_date),
      Description: e.description || "",
      Amount: e.amount || 0,
      "Expense Mode": e.expense_mode || "",
      "Expense Date": formatDisplayDate(e.expense_date),
      Remark: e.remark || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Indirect Expenses");
    XLSX.writeFile(wb, "IndirectExpenses.xlsx");
  };

  // Modal form
  const validateForm = () => {
    const errs = {};
    if (!form.description.trim()) errs.description = "Description is required";
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      errs.amount = "Enter a valid amount";
    if (!form.expense_mode) errs.expense_mode = "Expense mode is required";
    if (!form.expense_date) errs.expense_date = "Expense date is required";
    return errs;
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentFile(file);
      setForm((prev) => ({ ...prev, attachment: file.name }));
    }
  };

  const handleSubmit = async () => {
    const errs = validateForm();

    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const formData = new FormData();

      // Same insert endpoint handles update too — pass the real id when editing,
      // 0 when adding a new expense.
      formData.append("indirect_expense_id", editingId ? editingId : 0);
      formData.append("amount", parseFloat(form.amount));
      formData.append("expense_date", form.expense_date);
      formData.append("created_date", new Date().toISOString());
      formData.append("added_by", storedToken?.username || "");
      formData.append("remark", form.remark || "");
      formData.append("expense_mode", form.expense_mode);
      formData.append("description", form.description);

      // ACTUAL FILE — only append if the user picked a new one.
      // On edit, if they didn't change the attachment, we don't resend it;
      // the API should retain the existing attachment when none is provided.
      if (attachmentFile) {
        formData.append("attachment", attachmentFile);
      }

      const cleanBasePost = (baseAPI || "").replace(/\/+$/, "");

      const res = await fetch(
        `${cleanBasePost}/userpost/AddIndirectExpense`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${storedToken} `,
            // DO NOT SET CONTENT-TYPE HERE
          },
          body: formData,
        }
      );

      if (!res.ok) throw new Error("Failed to save expense");

      setSubmitSuccess(editingId ? "Expense updated successfully!" : "Expense added successfully!");

      setTimeout(() => {
        setShowModal(false);
        resetModal();
        fetchExpenses();
      }, 1200);

    } catch (err) {
      setSubmitError(editingId ? "Failed to update expense. Please try again." : "Failed to add expense. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetModal = () => {
    setForm({
      description: "",
      amount: "",
      expense_mode: "Bank Transfer",
      expense_date: "",
      remark: "",
      attachment: "",
    });
    setFormErrors({});
    setAttachmentFile(null);
    setSubmitError("");
    setSubmitSuccess("");
    setEditingId(null);
  };

  const openModal = () => {
    resetModal();
    setShowModal(true);
  };

  // Open the modal pre-filled with an existing expense's data
  const openEditModal = (exp) => {
    setFormErrors({});
    setAttachmentFile(null);
    setSubmitError("");
    setSubmitSuccess("");
    setEditingId(exp.indirect_expense_id);
    setForm({
      description: exp.description || "",
      amount: exp.amount != null ? String(exp.amount) : "",
      expense_mode: exp.expense_mode || "Bank Transfer",
      expense_date: formatDate(exp.expense_date),
      remark: exp.remark || "",
      attachment: exp.attachment || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetModal();
  };

  // Delete flow
  const openDeleteConfirm = (exp) => {
    setDeleteError("");
    setDeleteTarget(exp);
  };

  const closeDeleteConfirm = () => {
    setDeleteTarget(null);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const cleanBase = (baseAPI || "").replace(/\/+$/, "");
      const res = await fetch(`${cleanBase}/userpost/DeleteIndirectexpense`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${storedToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          indirect_expense_id: deleteTarget.indirect_expense_id,
        }),
      });

      if (!res.ok) throw new Error("Failed to delete expense");

      setDeleteTarget(null);
      fetchExpenses();
    } catch (err) {
      setDeleteError("Failed to delete expense. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="d-flex flex-row app">
      <MiniDrawer />

      <div
        className="container-fluid"
        style={{ padding: "4rem 1rem 1rem 1rem", backgroundColor: "#F4F4F4" }}
      >
        <div className="d-flex flex-column w-100" style={{ backgroundColor: "#F4F4F4" }}>
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
            <div>
              <h1 style={{ fontSize: "24px", marginBottom: "2rem" }}>
                Accounts & Finance | <HomeOutlinedIcon />{" "}
                <span style={{ fontSize: "20px" }}>&gt; Indirect Expense</span>
              </h1>
            </div>
          </div>
          <div style={{ flex: 1, padding: "0 0 20px 0", minWidth: 0 }}>

            {/* Page Card */}
            <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 16px rgba(108,99,255,0.07)", }}>
              {/* Header Bar */}
              <div style={{ background: "linear-gradient(90deg, #5b3fa6 0%, #7c5cbf 100%)", padding: "10px 20px", borderRadius: "14px 14px 0 0" }}>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 20, letterSpacing: 0.2 }}>Indirect Expense</span>
              </div>

              {/* Filters + Actions Row */}
              <div style={{ padding: "18px 28px 10px 28px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {/* Date Range Picker */}
                <div ref={datePickerRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowDatePicker((v) => !v)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      border: "1.5px solid #ddd", borderRadius: 8, background: "#fff",
                      padding: "8px 16px", cursor: "pointer", fontSize: 13, color: "#444",
                      fontWeight: 500, minWidth: 200, justifyContent: "space-between",
                      boxShadow: showDatePicker ? "0 0 0 2px #a78bfa55" : "none",
                      transition: "box-shadow 0.2s"
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c5cbf" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                      {getDateRangeLabel()}
                    </span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                  </button>
                  {showDatePicker && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 10000, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", borderRadius: 12, overflow: "hidden", border: "1px solid #ede9fe" }}>
                      <DateRangePicker
                        ranges={dateRange}
                        onChange={handleDateRangeChange}
                        rangeColors={["#6c63ff"]}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        direction="vertical"
                      />
                      <div style={{ background: "#fff", padding: "8px 16px 12px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button onClick={clearDateRange} style={{ padding: "6px 16px", border: "1px solid #ddd", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13, color: "#666" }}>Clear</button>
                        <button onClick={() => setShowDatePicker(false)} style={{ padding: "6px 18px", border: "none", borderRadius: 6, background: "#6c63ff", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Apply</button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Expense Mode Filter */}
                <div ref={modeDropdownRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowModeDropdown((v) => !v)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      border: "1.5px solid #ddd", borderRadius: 8, background: "#fff",
                      padding: "8px 16px", cursor: "pointer", fontSize: 13, color: "#444",
                      fontWeight: 500, minWidth: 160, justifyContent: "space-between",
                      boxShadow: showModeDropdown ? "0 0 0 2px #a78bfa55" : "none",
                    }}
                  >
                    {expenseMode}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                  </button>
                  {showModeDropdown && (
                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1000, background: "#fff", border: "1px solid #ede9fe", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", minWidth: 180, overflow: "hidden" }}>
                      {EXPENSE_MODES.map((mode) => (
                        <div
                          key={mode}
                          onClick={() => { setExpenseMode(mode); setShowModeDropdown(false); }}
                          style={{
                            padding: "10px 18px", cursor: "pointer", fontSize: 13,
                            background: expenseMode === mode ? "#ede9fe" : "#fff",
                            color: expenseMode === mode ? "#6c63ff" : "#333",
                            fontWeight: expenseMode === mode ? 600 : 400,
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={e => { if (expenseMode !== mode) e.currentTarget.style.background = "#f9f7ff"; }}
                          onMouseLeave={e => { if (expenseMode !== mode) e.currentTarget.style.background = "#fff"; }}
                        >
                          {mode}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Spacer */}
                <div style={{ flex: 1 }} />

                {/* Add Expense Button */}
                <button
                  onClick={openModal}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    background: "linear-gradient(90deg,#5b3fa6,#7c5cbf)",
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "9px 22px", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", boxShadow: "0 2px 10px rgba(108,99,255,0.18)",
                    transition: "opacity 0.2s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                  Add Expense
                </button>

                {/* Download Excel Button */}
                <button
                  onClick={handleDownloadExcel}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    background: "#fff", color: "#5b3fa6",
                    border: "1.5px solid #a78bfa", borderRadius: 8,
                    padding: "9px 18px", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", transition: "background 0.2s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#ede9fe"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5b3fa6" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  Download Excel
                </button>
              </div>

              {/* Error Banner */}
              {error && (
                <div style={{ margin: "0 28px 10px", padding: "10px 16px", background: "#fee2e2", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
                  {error}
                </div>
              )}
              {deleteError && (
                <div style={{ margin: "0 28px 10px", padding: "10px 16px", background: "#fee2e2", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
                  {deleteError}
                </div>
              )}

              {/* Table */}
              <div style={{ padding: "0 0 8px 0", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f3f0ff" }}>
                      {["S.No", "Date (Expense Added)", "Description", "Amount", "Expense Mode", "Expense Date", "Remark", "document", "Actions"].map((col) => (
                        <th key={col} style={{
                          padding: "12px 20px", textAlign: "left", fontWeight: 700,
                          color: "#5b3fa6", borderBottom: "2px solid #ede9fe",
                          whiteSpace: "nowrap", letterSpacing: 0.1, fontSize: 13
                        }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={9} style={{ textAlign: "center", padding: "50px 0", color: "#aaa" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "#7c5cbf" }}>
                          <svg style={{ animation: "spin 1s linear infinite" }} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>
                          Loading expenses...
                        </div>
                      </td></tr>
                    ) : expenses.length === 0 ? (
                      <tr><td colSpan={9} style={{ textAlign: "center", padding: "50px 0", color: "#bbb" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d4d4d4" strokeWidth="1.5"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                          <span style={{ fontSize: 14 }}>No expenses found</span>
                        </div>
                      </td></tr>
                    ) : (
                      expenses.map((exp, idx) => (
                        <tr key={exp.indirect_expense_id || idx}
                          style={{ borderBottom: "1px solid #f0ebff", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#faf8ff"}
                          onMouseLeave={e => e.currentTarget.style.background = ""}
                        >
                          <td style={{ padding: "13px 20px", color: "#888" }}>{idx + 1}</td>
                          <td style={{ padding: "13px 20px" }}>{formatDisplayDate(exp.created_date)}</td>
                          <td style={{ padding: "13px 20px", fontWeight: 500 }}>{exp.description || "-"}</td>
                          <td style={{ padding: "13px 20px", fontWeight: 600, color: "#5b3fa6" }}>{formatCurrency(exp.amount)}</td>
                          <td style={{ padding: "13px 20px" }}>
                            <span style={{
                              background: "#ede9fe", color: "#6c63ff",
                              borderRadius: 20, padding: "3px 12px",
                              fontSize: 12, fontWeight: 600
                            }}>{exp.expense_mode || "-"}</span>
                          </td>
                          <td style={{ padding: "13px 20px" }}>{formatDisplayDate(exp.expense_date)}</td>
                          <td style={{ padding: "13px 20px", color: "#666" }}>{exp.remark || "-"}</td>
                          <td style={{ padding: "13px 20px" }}>
                            {exp.attachment ? (
                              <a
                                href={`${baseImageURL.replace(/\/+$/, "")}/${exp.attachment}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 12px",
                                  background: "#ede9fe",
                                  color: "#5b3fa6",
                                  borderRadius: 6,
                                  textDecoration: "none",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td style={{ padding: "13px 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <button
                                onClick={() => openEditModal(exp)}
                                title="Edit"
                                style={{
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  width: 30, height: 30, border: "1px solid #ddd6fe", borderRadius: 6,
                                  background: "#fff", cursor: "pointer", color: "#5b3fa6"
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f0ff"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => openDeleteConfirm(exp)}
                                title="Delete"
                                style={{
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  width: 30, height: 30, border: "1px solid #fecaca", borderRadius: 6,
                                  background: "#fff", cursor: "pointer", color: "#dc2626"
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Expense Modal */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(30,20,60,0.45)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }} onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={{
            background: "#fff", borderRadius: 16,
            boxShadow: "0 24px 64px rgba(91,63,166,0.18)",
            width: "100%", maxWidth: 520,
            maxHeight: "90vh", overflowY: "auto",
            animation: "modalIn 0.22s cubic-bezier(.4,0,.2,1)"
          }}>
            {/* Modal Header */}
            <div style={{
              background: "linear-gradient(90deg,#5b3fa6,#7c5cbf)",
              padding: "18px 28px", borderRadius: "16px 16px 0 0",
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
                {editingId ? "Edit Expense" : "Add New Expense"}
              </span>
              <button onClick={closeModal} style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px 28px 28px" }}>
              {submitSuccess && (
                <div style={{ marginBottom: 16, padding: "10px 16px", background: "#dcfce7", borderRadius: 8, color: "#166534", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                  {submitSuccess}
                </div>
              )}
              {submitError && (
                <div style={{ marginBottom: 16, padding: "10px 16px", background: "#fee2e2", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>{submitError}</div>
              )}

              {/* Description */}
              <Field label="Description" required error={formErrors.description}>
                <input
                  type="text"
                  placeholder="Enter expense description"
                  value={form.description}
                  onChange={e => handleFormChange("description", e.target.value)}
                  style={inputStyle(formErrors.description)}
                />
              </Field>

              {/* Amount */}
              <Field label="Amount" required error={formErrors.amount}>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={form.amount}
                  onChange={e => handleFormChange("amount", e.target.value)}
                  style={inputStyle(formErrors.amount)}
                  min="0"
                />
              </Field>

              {/* Expense Mode */}
              <Field label="Expense Mode" required error={formErrors.expense_mode}>
                <select
                  value={form.expense_mode}
                  onChange={e => handleFormChange("expense_mode", e.target.value)}
                  style={{ ...inputStyle(formErrors.expense_mode), appearance: "auto" }}
                >
                  {EXPENSE_MODES.filter(m => m !== "All").map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </Field>

              {/* Expense Date */}
              <Field label="Expense Date" required error={formErrors.expense_date}>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={e => handleFormChange("expense_date", e.target.value)}
                  style={inputStyle(formErrors.expense_date)}
                />
              </Field>

              {/* Remark */}
              <Field label="Remark" error={formErrors.remark}>
                <textarea
                  placeholder="Enter any additional remarks"
                  value={form.remark}
                  onChange={e => handleFormChange("remark", e.target.value)}
                  rows={3}
                  style={{ ...inputStyle(), resize: "vertical", minHeight: 80 }}
                />
              </Field>

              {/* Upload Document */}
              <Field label="Upload Document">
                <label style={{
                  display: "flex", alignItems: "center", gap: 10,
                  border: "1.5px dashed #c4b5fd", borderRadius: 8,
                  padding: "12px 16px", cursor: "pointer",
                  background: "#faf8ff", transition: "background 0.2s"
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c5cbf" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  <span style={{ fontSize: 13, color: attachmentFile ? "#5b3fa6" : "#999", fontWeight: attachmentFile ? 500 : 400 }}>
                    {attachmentFile ? attachmentFile.name : (form.attachment ? form.attachment : "Click to upload document")}
                  </span>
                  <input type="file" hidden onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                </label>
                {editingId && form.attachment && !attachmentFile && (
                  <span style={{ display: "block", marginTop: 6, fontSize: 12, color: "#999" }}>
                    Existing document will be kept unless you upload a new one.
                  </span>
                )}
              </Field>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: "100%", marginTop: 8,
                  background: submitting ? "#c4b5fd" : "linear-gradient(90deg,#5b3fa6,#7c5cbf)",
                  color: "#fff", border: "none", borderRadius: 8,
                  padding: "12px 0", fontSize: 15, fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 12px rgba(108,99,255,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "opacity 0.2s"
                }}
              >
                {submitting ? (
                  <>
                    <svg style={{ animation: "spin 1s linear infinite" }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" /></svg>
                    {editingId ? "Updating..." : "Saving..."}
                  </>
                ) : (editingId ? "Update Expense" : "Save Expense")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 2100,
          background: "rgba(30,20,60,0.45)", backdropFilter: "blur(2px)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }} onClick={(e) => { if (e.target === e.currentTarget && !deleting) closeDeleteConfirm(); }}>
          <div style={{
            background: "#fff", borderRadius: 14,
            boxShadow: "0 24px 64px rgba(91,63,166,0.18)",
            width: "100%", maxWidth: 400, padding: "24px 26px",
            animation: "modalIn 0.22s cubic-bezier(.4,0,.2,1)"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%", background: "#fee2e2",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5">
                  <path d="M12 9v4M12 17h.01" />
                  <path d="M10.29 3.86l-8.18 14.18A2 2 0 0 0 3.82 21h16.36a2 2 0 0 0 1.71-2.96L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#1f2937" }}>Delete Expense?</span>
            </div>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 20px" }}>
              This will permanently delete{" "}
              <strong>{deleteTarget.description || "this expense"}</strong>
              {deleteTarget.amount != null ? ` (${formatCurrency(deleteTarget.amount)})` : ""}.
              This action cannot be undone.
            </p>
            {deleteError && (
              <div style={{ marginBottom: 14, padding: "10px 14px", background: "#fee2e2", borderRadius: 8, color: "#b91c1c", fontSize: 13 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={closeDeleteConfirm}
                disabled={deleting}
                style={{
                  padding: "9px 18px", border: "1px solid #ddd", borderRadius: 8,
                  background: "#fff", cursor: deleting ? "not-allowed" : "pointer",
                  fontSize: 13, fontWeight: 600, color: "#444"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                style={{
                  padding: "9px 18px", border: "none", borderRadius: 8,
                  background: deleting ? "#fca5a5" : "#dc2626", color: "#fff",
                  cursor: deleting ? "not-allowed" : "pointer",
                  fontSize: 13, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 6
                }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes modalIn { from { opacity: 0; transform: translateY(-20px) scale(0.97); } to { opacity: 1; transform: none; } }
  `}</style>
    </div>
  );
}

// Helper components
function Field({ label, required, error, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: "#374151" }}>
        {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
      </label>
      {children}
      {error && <span style={{ color: "#ef4444", fontSize: 12, marginTop: 4, display: "block" }}>{error}</span>}
    </div>
  );
}

function inputStyle(error) {
  return {
    width: "100%", padding: "10px 14px", fontSize: 13,
    border: `1.5px solid ${error ? "#fca5a5" : "#e5e7eb"} `,
    borderRadius: 8, outline: "none", boxSizing: "border-box",
    background: "#fff", color: "#222", transition: "border 0.2s",
    fontFamily: "inherit"
  };
}
